import { Static, Type } from '@sinclair/typebox';

export type DataSourceKind = 'request' | 'feedback' | 'structure';
export type DataSourceType = 'ftp' | 'api' | 'email' | 'import';

export type DataSourceParams =
  | FtpDataSourceParams
  | ImportDataSourceParams
  | ApiExcelDataSourceParams
  | ApiJsonDataSourceParams
  | EmailDataSourceParams;

export type ImportDataSourceParams = {
  importColumns?: string[];
};

export const FtpDataSourceDecryptActionSchema = Type.Object({
  action: Type.Literal('decrypt'),
  algorithm: Type.Optional(Type.Union([Type.Literal('aes-256-cbc')])),
  glob: Type.Optional(Type.String()),
  removeFile: Type.Boolean(),
  key: Type.String(), // Base64 encoded
});
export type FtpDataSourceDecryptAction = Static<
  typeof FtpDataSourceDecryptActionSchema
>;

export const FtpDataSourceCleaningActionSchema = Type.Object({
  action: Type.Literal('clean'),
  removeFile: Type.Boolean(),
  cleaningRule: Type.String(), // Base64 encoded
});
export type FtpDataSourceCleaningAction = Static<
  typeof FtpDataSourceCleaningActionSchema
>;

const FtpDataSourcePostProcessActionSchema = Type.Union([
  FtpDataSourceDecryptActionSchema,
  FtpDataSourceCleaningActionSchema,
]);

export const FtpDataSourceParamsSchema = Type.Object({
  connection: Type.Object({
    host: Type.String(),
    port: Type.Number(),
    username: Type.String(),
    encryptedPassword: Type.Optional(Type.String()),
    unencryptedPassword: Type.Optional(Type.String()),
    protocol: Type.Union([
      Type.Literal('ftp'),
      Type.Literal('sftp'),
      Type.Literal('ftps'),
    ]),
  }),
  directories: Type.Object({
    processingDir: Type.String(),
    processedDir: Type.String(),
    rootDir: Type.String(),
    includes: Type.Array(Type.String()),
    deleteProcessed: Type.Boolean(),
  }),
  importColumns: Type.Array(Type.String()),
  postprocess: Type.Optional(Type.Array(FtpDataSourcePostProcessActionSchema)),
});
export type FtpDataSourceParams = Static<typeof FtpDataSourceParamsSchema>;

export type ApiJsonDataSourceParams = {
  importColumns?: string[]; // stays empty
  mapping: { source: string | number; destination: string }[]; // [{ source: src|0; destination: col1 }, ...] // json
};
export type ApiExcelDataSourceParams = {
  importColumns: string[]; // [col1, col2, ... ]
};

export type EmailDataSourceParams = {
  importColumns: string[];
  whitelistedEmail?: string;
};

export interface DataSource {
  id: string;
  name: string;
  reference?: string;
  kind: DataSourceKind;
  type: DataSourceType;
  organizationId: string;
  params?: DataSourceParams;
  private: boolean;
  active: boolean;
  interval: string;
  scheduledAt: Date;
  createdAt: Date;
  updatedAt: Date;
  deletedAt?: Date;
  strategy: string;
  engineVersion: number;
  defaultImportConfigurationId?: string;
}

export interface DataSourceAction {
  id: string;
  sourceId: string;
  priority: number;
  active: boolean;
  condition: any;
  actions: any;
  createdAt: Date;
  updatedAt: Date;
}

export type ImportFile = {
  filename?: string; // uuid
  filetype?: string;
  originalFileName?: string;
  generated?: boolean;
  api?: boolean;
};

export type ImportParams = ImportFile & {
  columns?: string[]; // names of import columns
};

export interface DataImport {
  id: string;
  dataSourceId: string;
  name?: string;
  key?: string;
  params: ImportParams;
  size: number;
  createdAt: Date;
  updatedAt: Date;
  deletedAt?: Date;
}

export interface DataRow {
  id: number;
  rowNumber?: number;
  dataSourceId: string;
  dataImportId?: string;
  venueReference?: string;
  interactionReference?: string;
  interactionSource?: string;
  feedbackSource?: string;
  employeeReference?: string;
  organizationUnitReference?: string;
  profileReference?: string;
  reason?: string;
  source?: string;
  templateReference?: string;
  notificationMethod?: string;
  customerReference?: string;
  customerPhone?: string;
  customerEmail?: string;
  customerSalutation?: string;
  customerName?: string;
  customerFirstName?: string;
  customerLastName?: string;
  customerGender?: string;
  customerLanguage?: string;
  customerCountry?: string;
  customerAttribute1?: string;
  customerAttribute2?: string;
  customerAttribute3?: string;
  customerAttribute4?: string;
  customerAttribute5?: string;
  customerAttribute6?: string;
  customerAttribute7?: string;
  customerAttribute8?: string;
  customerAttribute9?: string;
  customerAttribute10?: string;
  customerAttribute11?: string;
  customerAttribute12?: string;
  customerAttribute13?: string;
  customerAttribute14?: string;
  customerAttribute15?: string;
  customerAttribute16?: string;
  customerAttribute17?: string;
  customerAttribute18?: string;
  customerAttribute19?: string;
  customerAttribute20?: string;
  interactionAttribute1?: string;
  interactionAttribute2?: string;
  interactionAttribute3?: string;
  interactionAttribute4?: string;
  interactionAttribute5?: string;
  interactionAttribute6?: string;
  interactionAttribute7?: string;
  interactionAttribute8?: string;
  interactionAttribute9?: string;
  interactionAttribute10?: string;
  interactionAttribute11?: string;
  interactionAttribute12?: string;
  interactionAttribute13?: string;
  interactionAttribute14?: string;
  interactionAttribute15?: string;
  interactionAttribute16?: string;
  interactionAttribute17?: string;
  interactionAttribute18?: string;
  interactionAttribute19?: string;
  interactionAttribute20?: string;
  interactionAttribute21?: string;
  interactionAttribute22?: string;
  interactionAttribute23?: string;
  interactionAttribute24?: string;
  interactionAttribute25?: string;
  interactionAttribute26?: string;
  interactionAttribute27?: string;
  interactionAttribute28?: string;
  interactionAttribute29?: string;
  interactionAttribute30?: string;
  interactionAttribute31?: string;
  interactionAttribute32?: string;
  interactionAttribute33?: string;
  interactionAttribute34?: string;
  interactionAttribute35?: string;
  interactionAttribute36?: string;
  interactionAttribute37?: string;
  interactionAttribute38?: string;
  interactionAttribute39?: string;
  interactionAttribute40?: string;
  interactionAttribute41?: string;
  interactionAttribute42?: string;
  interactionAttribute43?: string;
  interactionAttribute44?: string;
  interactionAttribute45?: string;
  interactionAttribute46?: string;
  interactionAttribute47?: string;
  interactionAttribute48?: string;
  interactionAttribute49?: string;
  interactionAttribute50?: string;
  interactionAttribute51?: string;
  interactionAttribute52?: string;
  interactionAttribute53?: string;
  interactionAttribute54?: string;
  interactionAttribute55?: string;
  anonymization?: string;
  data?: string[];
  jsonData?: any;
  urlPath?: string;
  createdAt: Date;
  updatedAt: Date;
  deletedAt?: Date;
  employmentFirstName?: string;
  employmentLastName?: string;
  employmentEmail?: string;
  employmentPosition?: string;
  index1?: string;
  index2?: string;
  index3?: string;
  customerId?: string;
  interactionId?: string;
  venueId?: string;
  employmentId?: string;
  profileId?: string;
  organizationUnitId?: string;
  errorCode: number;
}

export type DataRowMapping = { source: string | number; destination: string }[];

export interface DataTriggerInvocation {
  id: string;
  dataRowId: number;
  dataTriggerId: string;
  splitterId: string;
  result?: string;
  lowerDateLimit?: Date;
  upperDateLimit?: Date;
  lowerRowLimit?: number;
  upperRowLimit?: number;
  scheduledAt?: Date;
  startedAt?: Date;
  finishedAt?: Date;
  createdAt: Date;
  updatedAt: Date;
  deletedAt?: Date;
}

export interface DataTriggerInvocationStat {
  id: string;
  invocationId: string;
  triggerId: string;
  totalCount: number | null;
  matchedCount: number | null;
  deliveredCount: number | null;
  createdAt: Date;
  updatedAt: Date;
}

export interface DataImportCampaignStat {
  id: string;
  name?: string; // this is not part of db model, however it will
  dataImportId: string;
  campaignId: string;
  createdAt: Date;
  updatedAt: Date;
  size: number;
  matchedCount: number;
  sentCount: number;
  successCount: number;
  failureCount: number;
  lastProcessedRow: number;
  errorMessages?: Record<string, any>;
  successKeys?: Record<string, any>;
}

export interface DataImportTriggerStat {
  id: string;
  dataImportId: string;
  dataTriggerId: string;
  createdAt: Date;
  updatedAt: Date;
  matchedCount: number;
  sentCount: number;
  successCount: number;
  failureCount: number;
  lastProcessedRow: number;
  errorMessages?: Record<string, string>;
}

export interface DataImportStatsRecalcRequest {
  id: string;
  dataTriggerInvocationId: string;
  createdAt: Date;
  updatedAt: Date;
}
